import { httpRequest } from "../utils/httpClient";
import { ApacuanaAPIError } from "../errors/index";
import { getConfig } from "../config/index";
import ApacuanaSuccess from "../success";

/**
 * @typedef {object} GetCustomerData
 * @property {string} token - El token de sesión del usuario.
 * @property {object} userData - Los datos del usuario obtenidos.
 */

/**
 * @typedef {object} GetCustomerResponse
 * @property {true} success - Indica que la operación fue exitosa.
 * @property {GetCustomerData} data - El payload de la respuesta.
 */

/**
 * Obtiene el token de un usuario a través de una petición POST.
 * Este método es útil para endpoints que requieren datos en el cuerpo de la petición
 * para buscar un usuario, como un ID de sesión o un token de acceso.
 *
 * @returns {Promise<GetCustomerResponse>} Una promesa que resuelve a un objeto con la respuesta exitosa.
 * @throws {Error} Si los parámetros de entrada son inválidos.
 * @throws {ApacuanaAPIError} Si ocurre un error en la API de Apacuana.
 */
export const getCustomer = async () => {
  const { verificationId, customerId } = getConfig();

  if (!verificationId || !customerId) {
    throw new ApacuanaAPIError(
      "Both 'verificationId' and 'customerId' must be configured.",
      400,
      "CONFIGURATION_ERROR"
    );
  }

  const body = {
    verificationid: verificationId,
    customerid: customerId,
  };

  try {
    const response = await httpRequest(
      "services/api/register/init",
      body,
      "POST"
    );

    if (!response.sessionid || !response.entry) {
      throw new ApacuanaAPIError(
        "The API response does not contain the user.",
        200,
        "INVALID_API_RESPONSE"
      );
    }

    return new ApacuanaSuccess({
      token: response.sessionid,
      userData: response.entry,
    });
  } catch (error) {
    if (error instanceof ApacuanaAPIError) {
      throw error;
    }
    throw new ApacuanaAPIError(
      `Unexpected failure getting token: ${error.message || "Unknown error"}`
    );
  }
};

/**
 * @typedef {object} CreateUserPayload
 * @property {string} usr - Correo electrónico del usuario.
 * @property {string} pwd - Contraseña del usuario.
 * @property {string} kinddoc - Tipo de documento de identidad (ej. 'V', 'P', 'E').
 * @property {string} doc - Número de documento de identidad.
 */

/**
 * @typedef {object} CreateUserResponse
 * @property {string} message - Mensaje de confirmación de la creación del usuario.
 */

/**
 * Crea un nuevo usuario en la plataforma de Apacuana.
 * @param {CreateUserPayload} userData - Objeto con los datos del usuario a crear.
 * @returns {Promise<ApacuanaSuccess>} Una promesa que resuelve a un objeto con la respuesta exitosa.
 * @throws {ApacuanaAPIError} Si los datos de entrada son inválidos o si ocurre un error en la API.
 */

export const createApacuanaUser = async (userData) => {
  try {
    const formData = new FormData();
    Object.keys(userData).forEach((key) => {
      const value = userData[key];
      const isRNFile =
        value &&
        typeof value === "object" &&
        value.uri &&
        value.name &&
        value.type;
      if (value instanceof File || isRNFile) {
        formData.append(key, value);
      } else if (value !== null && value !== undefined) {
        formData.append(key, String(value));
      }
    });

    const response = await httpRequest(
      "services/api/register/initial",
      formData,
      "POST"
    );

    return new ApacuanaSuccess({
      ...response,
    });
  } catch (error) {
    if (error instanceof ApacuanaAPIError) {
      throw error;
    }
    throw new ApacuanaAPIError(
      `Unexpected failure creating user: ${error.message || "Unknown error"}`
    );
  }
};
